/**
 * Web-only readable trip URLs. Device Sync / iOS still use the trip UUID.
 * Slugs are derived from the current title and are not stored on the trip.
 */

export const TRIP_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Path segments reserved by `/trips/new` and `/trips/shared`. */
export const RESERVED_TRIP_SLUGS = new Set(['new', 'shared']);

export interface TripUrlFields {
  id: string;
  title: string;
}

export function isTripUuid(value: string | null | undefined): boolean {
  return TRIP_UUID_RE.test(String(value ?? '').trim());
}

export function slugifyTripTitle(title: string | null | undefined): string {
  const slug = String(title ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');
  return slug || 'trip';
}

function baseTripSlug(title: string | null | undefined): string {
  const slug = slugifyTripTitle(title);
  return RESERVED_TRIP_SLUGS.has(slug) ? `${slug}-trip` : slug;
}

function shortTripId(id: string): string {
  return String(id)
    .trim()
    .toLowerCase()
    .replace(/-/g, '')
    .slice(0, 8);
}

/** Unique slug among the user's visible trips. Collisions append 8 hex chars of the id. */
export function tripWebSlug(
  trip: TripUrlFields,
  siblings: TripUrlFields[]
): string {
  const base = baseTripSlug(trip.title);
  const collisions = siblings.filter((other) => baseTripSlug(other.title) === base);
  if (collisions.length <= 1) return base;
  return `${base}-${shortTripId(trip.id)}`;
}

export function resolveTripFromRef<T extends TripUrlFields>(
  trips: T[],
  ref: string | null | undefined
): T | null {
  const raw = String(ref ?? '').trim();
  if (!raw) return null;
  if (isTripUuid(raw)) {
    const id = raw.toLowerCase();
    return trips.find((trip) => String(trip.id).trim().toLowerCase() === id) ?? null;
  }
  return trips.find((trip) => tripWebSlug(trip, trips) === raw) ?? null;
}

export function tripWebPath(
  trip: TripUrlFields,
  siblings: TripUrlFields[],
  suffix = ''
): string {
  const slug = tripWebSlug(trip, siblings);
  if (!suffix) return `/trips/${slug}`;
  if (suffix.startsWith('/') || suffix.startsWith('?')) return `/trips/${slug}${suffix}`;
  return `/trips/${slug}/${suffix}`;
}
